import apiClient from './api';
import type { Book, BookCreate, BookUpdate, BookLookup, BulkUpload, BulkUploadCreate, BulkUploadUpdate, BulkUploadVerification, MustReadBook } from '../types';

export interface ISBNExtractResponse {
  isbns: string[];
  message: string;
}

export const bookService = {
  getAllBooks: async (params?: {
    skip?: number;
    limit?: number;
    search?: string;
    category?: string;
  }): Promise<Book[]> => {
    const response = await apiClient.get('/api/books', { params });
    return response.data;
  },

  getRecentBooks: async (limit: number = 10): Promise<Book[]> => {
    const response = await apiClient.get('/api/books/recent', { params: { limit } });
    return response.data;
  },

  getMustReadBooks: async (limit: number = 10): Promise<MustReadBook[]> => {
    const response = await apiClient.get('/api/books/must-read', { params: { limit } });
    return response.data;
  },

  getBook: async (id: number): Promise<Book> => {
    const response = await apiClient.get(`/api/books/${id}`);
    return response.data;
  },

  lookupBookByIsbn: async (isbn: string): Promise<BookLookup> => {
    const response = await apiClient.get(`/api/books/lookup/isbn/${isbn}`);
    return response.data;
  },

  createBook: async (bookData: BookCreate & { image?: File | string | null }): Promise<Book> => {
    const formData = new FormData();
    formData.append('title', bookData.title);
    formData.append('author_name', bookData.author_name);
    if (bookData.isbn) formData.append('isbn', bookData.isbn.toString());
    if (bookData.publisher) formData.append('publisher', bookData.publisher.toString());
    if (bookData.publication_year) formData.append('publication_year', bookData.publication_year.toString());
    if (bookData.category) formData.append('category', bookData.category.toString());
    if (bookData.description) formData.append('description', bookData.description.toString());

    // Handle image: either file upload or existing image path
    if (bookData.image) {
      if (bookData.image instanceof File) {
        formData.append('image', bookData.image);
      } else if (typeof bookData.image === 'string') {
        formData.append('image_path', bookData.image);
      }
    }

    const response = await apiClient.post('/api/books', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  updateBook: async (id: number, bookData: BookUpdate & { image?: File | string | null }): Promise<Book> => {
    const formData = new FormData();
    if (bookData.title !== undefined) formData.append('title', bookData.title);
    if (bookData.author_name !== undefined) formData.append('author_name', bookData.author_name);
    if (bookData.isbn !== undefined && bookData.isbn !== null) formData.append('isbn', bookData.isbn.toString());
    if (bookData.publisher !== undefined && bookData.publisher !== null) formData.append('publisher', bookData.publisher.toString());
    if (bookData.publication_year !== undefined && bookData.publication_year !== null) formData.append('publication_year', bookData.publication_year.toString());
    if (bookData.category !== undefined && bookData.category !== null) formData.append('category', bookData.category.toString());
    if (bookData.description !== undefined && bookData.description !== null) formData.append('description', bookData.description.toString());

    // Handle image: either file upload or existing image path
    if (bookData.image) {
      if (bookData.image instanceof File) {
        formData.append('image', bookData.image);
      } else if (typeof bookData.image === 'string') {
        formData.append('image_path', bookData.image);
      }
    }

    const response = await apiClient.put(`/api/books/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteBook: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/books/${id}`);
  },

  extractISBNFromImage: async (imageFile: File): Promise<ISBNExtractResponse> => {
    const formData = new FormData();
    formData.append('image', imageFile);

    const response = await apiClient.post('/api/books/extract-isbn', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Bulk Upload methods
  bulkUploadExtractISBN: async (barcodeImage: File): Promise<ISBNExtractResponse> => {
    const formData = new FormData();
    formData.append('barcode_image', barcodeImage);

    const response = await apiClient.post('/api/books/bulk-upload/extract-isbn', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  bulkUploadLookup: async (barcodeImage: File): Promise<BookLookup> => {
    const formData = new FormData();
    formData.append('barcode_image', barcodeImage);

    const response = await apiClient.post('/api/books/bulk-upload/lookup', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  submitBulkUpload: async (uploadData: BulkUploadCreate & { barcode_image: File; cover_image_path?: string }): Promise<BulkUpload> => {
    const formData = new FormData();
    formData.append('title', uploadData.title);
    formData.append('author_name', uploadData.author_name);
    if (uploadData.isbn) formData.append('isbn', uploadData.isbn.toString());
    if (uploadData.publisher) formData.append('publisher', uploadData.publisher.toString());
    if (uploadData.publication_year) formData.append('publication_year', uploadData.publication_year.toString());
    if (uploadData.category) formData.append('category', uploadData.category.toString());
    if (uploadData.description) formData.append('description', uploadData.description.toString());
    formData.append('barcode_image', uploadData.barcode_image);
    if (uploadData.cover_image_path) formData.append('cover_image_path', uploadData.cover_image_path);

    const response = await apiClient.post('/api/books/bulk-upload/submit', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getPendingBulkUploads: async (params?: { skip?: number; limit?: number }): Promise<BulkUpload[]> => {
    const response = await apiClient.get('/api/books/bulk-upload/pending', { params });
    return response.data;
  },

  getAllBulkUploads: async (params?: { skip?: number; limit?: number; status?: string }): Promise<BulkUpload[]> => {
    const response = await apiClient.get('/api/books/bulk-upload/all', { params });
    return response.data;
  },

  getBulkUpload: async (uploadId: number): Promise<BulkUpload> => {
    const response = await apiClient.get(`/api/books/bulk-upload/${uploadId}`);
    return response.data;
  },

  updateBulkUpload: async (uploadId: number, updateData: BulkUploadUpdate): Promise<BulkUpload> => {
    const response = await apiClient.put(`/api/books/bulk-upload/${uploadId}`, updateData);
    return response.data;
  },

  verifyBulkUpload: async (uploadId: number, verification: BulkUploadVerification): Promise<BulkUpload> => {
    const response = await apiClient.post(`/api/books/bulk-upload/${uploadId}/verify`, verification);
    return response.data;
  },

  retryBulkUpload: async (uploadId: number): Promise<BulkUpload> => {
    const response = await apiClient.post(`/api/books/bulk-upload/${uploadId}/retry`);
    return response.data;
  },

  retryAllErrorBulkUploads: async (): Promise<{ message: string; success_count: number; failed_count: number; results: Array<{ id: number; title: string; success: boolean; reason?: string }> }> => {
    const response = await apiClient.post('/api/books/bulk-upload/retry-all-errors');
    return response.data;
  },

  markBulkUploadAsDone: async (uploadId: number, markedAsDone: boolean): Promise<BulkUpload> => {
    const response = await apiClient.post(`/api/books/bulk-upload/${uploadId}/mark-as-done`, { marked_as_done: markedAsDone });
    return response.data;
  },

  // Background Bulk Upload methods
  submitBackgroundBulkUpload: async (barcodeImages: File[]): Promise<{ message: string; task_ids: string[]; count: number }> => {
    const formData = new FormData();
    barcodeImages.forEach((image) => {
      formData.append('barcode_images', image);
    });

    const response = await apiClient.post('/api/books/bulk-upload/background', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getBackgroundUploadStatus: async (taskIds?: string[]): Promise<{ tasks: Array<{ id: string; filename: string; status: string; message: string; book_title?: string; book_author?: string; isbn?: string }> } | { total: number; pending: number; processing: number; completed: number; failed: number }> => {
    const params = taskIds ? { task_ids: taskIds.join(',') } : {};
    const response = await apiClient.get('/api/books/bulk-upload/background-status', { params });
    return response.data;
  },

  updateBookShelf: async (id: number, shelfId: number | null): Promise<Book> => {
    const response = await apiClient.patch(`/api/books/${id}/shelf`, null, {
      params: { shelf_id: shelfId },
    });
    return response.data;
  },
};
